import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, StyleSheet, Text, View } from "react-native";
import type { Palette, PlanStarter } from "@jackedlogs/shared";
import { Button, resolveTheme, useBreakpoint } from "@jackedlogs/ui";
import { useThemePalette } from "./theme";
import { useAuth, type AuthUser } from "./auth/AuthContext";
import { PlanScreen } from "./PlanScreen";
import { WorkoutsScreen } from "./WorkoutsScreen";
import { ProgressScreen } from "./ProgressScreen";
import { SettingsScreen, type SettingsSubKey } from "./SettingsScreen";
import { InboxScreen } from "./InboxScreen";
import { CoachDashboardScreen } from "./CoachDashboardScreen";
import { AccountMenu } from "./AccountMenu";
import { useExperience } from "./experience";
import { useCoachStatus } from "./useCoachStatus";
import { Sidebar } from "./shell/Sidebar";
import { TopBar } from "./shell/TopBar";
// The Me sections (ME-1 / MS-1) are the athlete's own data, reached from the
// account menu; their content pane is extracted to keep this shell small.
import { MeContentPane } from "./shell/MeContentPane";
import type { MeKey } from "./shell/meNav";
import { useCreateHubWeb, type ComposerRequest } from "./useCreateHubWeb";

// Signed-in chrome for web-rnw. A TopBar (brand · Create · account menu, the
// account pinned right) sits above BOTH layouts of the SAME nav state, chosen
// by viewport width (useBreakpoint), never by platform:
//   • wide (desktop console) → a left nav rail (Sidebar) + content beside it.
//   • narrow (phone / narrow browser) → a top tab bar, then content below.
// The account menu (role switch + Me + Settings + sign-out) is the identity
// slot in both; the rail/tab bar carries only the surface nav. State-based
// navigation (no router); URL routing can layer on later. Each screen stays a
// plain RN tree so it also mounts under mobile's react-navigation; this shell
// is the only web-rnw-specific piece.
type Surface = { key: string; label: string; render: () => ReactNode };

export function AppShell({ user }: { user: AuthUser }) {
  const { signOut } = useAuth();
  const { t } = useTranslation();
  const { dark, p, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const styles = makeStyles(p);

  // Experience = role-gated surface set (athlete / coach / admin). Settings
  // lives in ALL; the feedback inbox is the admin experience, the team overview
  // is the coach experience (CO-8) — neither is a stray athlete tab. Coach shows
  // once the caller has an active coach edge.
  // One edge read serves both: the gate (isCoach) and the dashboard's roster.
  // `ready` keeps the experience guard from clobbering a persisted 'coach'
  // choice before the edges land (CO-15).
  const { isCoach, athleteSubs, ready: coachReady } = useCoachStatus(user.sub);
  const { available, active: experience, setActive: setExperience } =
    useExperience(user.admin, isCoach, coachReady);

  // Active surface key WITHIN the experience. Declared before `surfaces` so
  // the Plan tab's onOpenProgress seam can flip to "progress".
  const [activeTab, setActiveTab] = useState("plan");

  // Me (ME-1) is a second nav axis, orthogonal to the rail tabs: the avatar-menu
  // picks a Me section, and while one is open it takes over the content pane.
  // Null = showing the active tab. Selecting any tab clears it (selectTab).
  const [meSub, setMeSub] = useState<MeKey | null>(null);
  const selectTab = useCallback((key: string) => {
    setMeSub(null);
    setActiveTab(key);
  }, []);

  // Cross-tab deep-link into a Settings sub-screen (e.g. the Logger's
  // bodyweight nudge → Body). Consume-then-clear, same shape as
  // composerRequest below; the shared renderSettings() closure wires it into
  // every "settings" surface entry. Both memoized so the surfaces useMemo
  // below only recomputes when settingsSub actually changes, not every
  // render (selectTab/setSettingsSub are stable setters).
  const [settingsSub, setSettingsSub] = useState<SettingsSubKey | null>(null);
  const renderSettings = useCallback(
    () => (
      <SettingsScreen
        requestedSub={settingsSub}
        onRequestedSubHandled={() => setSettingsSub(null)}
      />
    ),
    [settingsSub],
  );
  const requestSetBodyweight = useCallback(() => {
    setSettingsSub("body");
    selectTab("settings");
  }, [selectTab]);

  // Create-hub (HR-S4w): the sidebar "Crea" CTA opens the same shared hub
  // mobile's tab-bar `+` does. Hub intents land here as a composer request
  // the Plan surface (program/plan) or Workouts surface (RW-18: workout)
  // consumes on mount (state-based nav — no router).
  const [composerRequest, setComposerRequest] =
    useState<ComposerRequest | null>(null);
  const { openHub, closeHub, hubNode } = useCreateHubWeb((req) => {
    setActiveTab(req.kind === "workout" ? "workouts" : "plan");
    setComposerRequest(req);
  });
  // Plan tab's "Start" (today's session / a program-highlight card) / "New
  // workout" — the SAME cross-tab dispatch the Create-hub's "New workout"
  // uses above, optionally carrying a pre-built PlanStarter. Passed down to
  // PlanScreen; useWorkoutStarter.ts builds the starter, this only routes
  // it to the Workouts tab.
  const requestStartWorkout = useCallback((starter?: PlanStarter) => {
    setActiveTab("workouts");
    setComposerRequest({ kind: "workout", ...(starter ? { planStarter: starter } : {}) });
  }, []);
  // The hub entry — the shell's one primary action, the web twin of the
  // mobile tab-bar `+` FAB. Shared <Button> primitive, never hand-rolled;
  // athlete experience only.
  const createCta =
    experience === "athlete" ? (
      <Button
        theme={theme}
        icon="plus"
        compact
        label={t("createHub.title", { defaultValue: "Create" })}
        onPress={openHub}
      />
    ) : null;

  // Each experience's nav tabs. Settings is shared across both. The athlete
  // set is the Plan hub + Progress; admin is the feedback inbox.
  const surfaces: Surface[] = useMemo(
    () =>
      experience === "coach"
        ? [
            {
              key: "coachDashboard",
              label: t("coach.dashboard.title"),
              render: () => (
                <CoachDashboardScreen
                  athleteSubs={coachReady ? athleteSubs : null}
                />
              ),
            },
            {
              key: "settings",
              label: t("mobile.tabs.settings", { defaultValue: "Settings" }),
              render: renderSettings,
            },
          ]
        : experience === "admin"
        ? [
            {
              key: "inbox",
              label: t("feedback.inboxTitle"),
              render: () => <InboxScreen dark={dark} />,
            },
            {
              key: "settings",
              label: t("mobile.tabs.settings", { defaultValue: "Settings" }),
              render: renderSettings,
            },
          ]
        : [
            {
              key: "plan",
              label: t("mobile.tabs.plan"),
              render: () => (
                <PlanScreen
                  onOpenProgress={() => setActiveTab("progress")}
                  composerRequest={composerRequest}
                  onComposerRequestHandled={() => setComposerRequest(null)}
                  onStartWorkout={requestStartWorkout}
                />
              ),
            },
            {
              key: "workouts",
              label: t("mobile.tabs.workouts"),
              render: () => (
                <WorkoutsScreen
                  composerRequest={composerRequest}
                  onComposerRequestHandled={() => setComposerRequest(null)}
                  onRequestSetBodyweight={requestSetBodyweight}
                />
              ),
            },
            {
              key: "progress",
              label: t("mobile.tabs.progress"),
              render: () => <ProgressScreen />,
            },
            {
              key: "settings",
              label: t("mobile.tabs.settings", { defaultValue: "Settings" }),
              render: renderSettings,
            },
          ],
    // composerRequest is read inside the plan surface's render closure —
    // it MUST invalidate the memo or PlanScreen mounts with a stale null.
    // athleteSubs + coachReady MUST be here: the coach surface closes over them,
    // and they arrive async. Leaving them out pinned the closure to the empty
    // roster it saw on the first render, so opening straight into coach showed a
    // dashboard of zeroes forever and only a re-memo (switching experience)
    // fixed it. `user.sub` used to stand here and is what hid the gap: nothing
    // in the memo reads it, so it never invalidated anything. Dropped
    // 2026-08-14, when web-rnw finally joined the lint gate and said so.
    // renderSettings/requestSetBodyweight/requestStartWorkout are their own
    // useCallbacks (stable unless settingsSub changes, or never for
    // requestStartWorkout) — listed so a real change still invalidates the
    // memo, the same stale-closure trap the comment above already names
    // (2026-08-14) for athleteSubs/coachReady.
    [
      experience,
      t,
      dark,
      composerRequest,
      athleteSubs,
      coachReady,
      renderSettings,
      requestSetBodyweight,
      requestStartWorkout,
    ],
  );

  // When the experience changes, point at its first tab (athlete→plan,
  // admin→inbox) so a stale key (e.g. "plan" while in admin) never leaves
  // the nav with nothing highlighted. The hub is athlete-only chrome —
  // close it too so it can't linger open across the switch.
  useEffect(() => {
    setActiveTab(
      experience === "admin"
        ? "inbox"
        : experience === "coach"
          ? "coachDashboard"
          : "plan",
    );
    setMeSub(null);
    closeHub();
  }, [experience, closeHub]);

  const current = surfaces.find((s) => s.key === activeTab) ?? surfaces[0]!;
  const { wide } = useBreakpoint();

  // The surface tabs the rail / nav bar shows. Settings is NOT among them — it
  // lives in the account menu now — but it stays a real surface (in `surfaces`)
  // so the account menu can open it via selectTab("settings").
  const navItems = surfaces.filter((s) => s.key !== "settings");

  // The account menu (identity slot), anchored top-right of the TopBar in both
  // layouts. It routes to the three axes the shell owns: a Me section (meSub),
  // a surface (Settings), or an experience switch. Wide shows the email; narrow
  // is avatar-only (compact).
  const accountMenu = (compact: boolean): ReactNode => (
    <AccountMenu
      email={user.email}
      activeKey={meSub}
      activeSettings={!meSub && current.key === "settings"}
      experiences={available}
      activeExperience={experience}
      onSelectMe={setMeSub}
      onSelectExperience={setExperience}
      onOpenSettings={() => selectTab("settings")}
      onSignOut={signOut}
      placement="bottom"
      align="right"
      compact={compact}
    />
  );

  // Desktop console: a top bar (brand · Create · account) over a row of the nav
  // rail + content. The role switch and Settings left the rail for the account
  // menu, so the rail is pure surface nav.
  if (wide) {
    return (
      <View style={styles.shell}>
        <TopBar p={p} brand="JackedLogs" cta={createCta} right={accountMenu(false)} />
        <View style={styles.shellRow}>
          <Sidebar
            p={p}
            items={navItems.map((s) => ({ key: s.key, label: s.label }))}
            // Settings isn't a rail row, so while it's the active surface no rail
            // row matches activeKey — correct, nothing highlights for it.
            activeKey={meSub ? "" : current.key}
            onSelect={selectTab}
          />
          <View style={styles.contentWide}>
            {meSub ? (
              <MeContentPane meSub={meSub} wide p={p} onBack={() => setMeSub(null)} />
            ) : (
              current.render()
            )}
          </View>
        </View>
        {experience === "athlete" ? hubNode : null}
      </View>
    );
  }

  // Narrow (phone / narrow browser): the same TopBar over a top tab bar, then
  // content. Role switch + Settings live in the account menu, so there's no
  // separate switcher bar and no Settings tab.
  return (
    <View style={styles.shell}>
      <TopBar
        p={p}
        brand="JackedLogs"
        cta={createCta}
        right={accountMenu(true)}
        maxWidth={CONTENT_MAX}
      />

      {navItems.length > 1 && (
        <View style={styles.navBar}>
          <View style={styles.navInner} accessibilityRole="tablist">
            {navItems.map((s) => {
              const on = s.key === activeTab && meSub === null;
              return (
                <Pressable
                  key={s.key}
                  style={[styles.tab, on && styles.tabOn]}
                  onPress={() => selectTab(s.key)}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: on }}
                  accessibilityLabel={s.label}
                >
                  <Text style={[styles.tabLabel, on && styles.tabLabelOn]}>
                    {s.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      )}

      <View style={styles.body}>
        <View style={styles.bodyInner}>
          {meSub ? (
            <MeContentPane meSub={meSub} wide={false} p={p} onBack={() => setMeSub(null)} />
          ) : (
            <>
              {/* Single-surface users have no nav bar to name the surface, so
                  the shell names it once here. Multi-surface = the nav tabs
                  already name it; screens never render their own title. */}
              {navItems.length === 1 && (
                <Text style={styles.soloTitle}>{current.label}</Text>
              )}
              {current.render()}
            </>
          )}
        </View>
      </View>
      {experience === "athlete" ? hubNode : null}
    </View>
  );
}

// Shared content column width — header, nav and body all centre to this
// so the console reads as one aligned column on wide screens.
const CONTENT_MAX = 960;

function makeStyles(p: Palette) {
  return StyleSheet.create({
    shell: { flex: 1, backgroundColor: p.bg },
    // Wide console: row of [rail | content]. Content reclaims the full
    // remaining width (the per-surface multi-column treatment lands in the
    // next phase); the rail stays put while the surface scrolls itself.
    shellRow: { flex: 1, flexDirection: "row", backgroundColor: p.bg },
    contentWide: {
      flex: 1,
      minWidth: 0,
      maxWidth: 1320,
      paddingHorizontal: 24,
      paddingVertical: 16,
    },
    navBar: { borderBottomWidth: 1, borderBottomColor: p.border },
    navInner: {
      width: "100%",
      maxWidth: CONTENT_MAX,
      marginHorizontal: "auto",
      flexDirection: "row",
      // Tabs can overflow a phone-width browser in the long locales (IT/DE) —
      // wrap instead of clipping.
      flexWrap: "wrap",
      gap: 8,
      paddingHorizontal: 12,
      paddingVertical: 8,
    },
    tab: {
      minHeight: 40,
      paddingHorizontal: 16,
      justifyContent: "center",
      borderRadius: 10,
      borderWidth: 1,
      borderColor: "transparent",
    },
    tabOn: { backgroundColor: p.surface2, borderColor: p.border },
    tabLabel: { color: p.ink3, fontSize: 15, fontWeight: "600" },
    tabLabelOn: { color: p.ink, fontWeight: "700" },
    body: { flex: 1 },
    bodyInner: {
      flex: 1,
      width: "100%",
      maxWidth: CONTENT_MAX,
      marginHorizontal: "auto",
    },
    soloTitle: {
      color: p.ink,
      fontSize: 20,
      fontWeight: "700",
      paddingHorizontal: 16,
      paddingTop: 16,
    },
  });
}
